import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { PhoneInputField } from '@/components/common/PhoneInputField';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { isValidPhone } from '@/lib/phone';

import {
  useContactoLaboratorio,
  useUpdateContactoLaboratorio,
} from '../hooks/useConfiguracion';

const contactoSchema = z.object({
  email: z
    .string()
    .min(1, 'Requerido')
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Email no válido'),
  whatsapp: z
    .string()
    .min(1, 'Requerido')
    .refine(isValidPhone, 'Número inválido. Usá formato internacional.'),
  direccion: z.string().min(1, 'Requerido').max(200, 'Máximo 200 caracteres'),
});

type ContactoFormValues = z.infer<typeof contactoSchema>;

const DEFAULT_VALUES: ContactoFormValues = { email: '', whatsapp: '', direccion: '' };

export function ContactoLaboratorioSection(): JSX.Element {
  const notify = useNotify();
  const query = useContactoLaboratorio();
  const mutation = useUpdateContactoLaboratorio();
  const [saveError, setSaveError] = useState<unknown>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, errors },
  } = useForm<ContactoFormValues>({
    resolver: zodResolver(contactoSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (query.data) {
      reset({
        email: query.data.email,
        whatsapp: query.data.whatsapp,
        direccion: query.data.direccion,
      });
    }
  }, [query.data, reset]);

  const isNotFound = isApiError(query.error) && query.error.isNotFound();
  const hasLoadError = query.isError && !isNotFound;
  const hasFormErrors = Object.keys(errors).length > 0;

  async function onSubmit(values: ContactoFormValues): Promise<void> {
    setSaveError(null);
    try {
      const updated = await mutation.mutateAsync(values);
      notify.success('Contacto actualizado.');
      reset({
        email: updated.email,
        whatsapp: updated.whatsapp,
        direccion: updated.direccion,
      });
    } catch (err) {
      if (isApiError(err) && err.isValidationError()) {
        err.validationErrors().forEach(({ field, message }) => {
          setError(field as keyof ContactoFormValues, { type: 'server', message });
        });
      } else {
        setSaveError(err);
      }
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Contacto del laboratorio
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Datos de contacto que aparecen en los informes y emails enviados.
      </Typography>

      {query.isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </Box>
      ) : hasLoadError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          }
        >
          {getErrorMessage(query.error)}
        </Alert>
      ) : (
        <>
          {saveError != null && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {getErrorMessage(saveError)}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email *"
                  type="email"
                  size="small"
                  fullWidth
                  placeholder="laboratorio@dpv.com"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <PhoneInputField
              control={control}
              name="whatsapp"
              label="WhatsApp *"
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <Controller
                control={control}
                name="direccion"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Dirección *"
                    size="small"
                    fullWidth
                    placeholder="Av. Colón 100, Tandil"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!isDirty || mutation.isPending || hasFormErrors}
              onClick={() => void handleSubmit(onSubmit)()}
              startIcon={
                mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {mutation.isPending ? 'Guardando...' : 'Guardar contacto'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
